import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, ButtonBase, Dialog, Stack, styled } from '@mui/material';
import {
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridRowId,
  GridSortModel,
} from '@mui/x-data-grid-premium';

import { ShipmentResponse } from '@/api/data-contracts';
import { ReactComponent as BoxesIcon } from '@/assets/icons/awbs-boxes-icon.svg';
import { useDebouncedValue } from '@/shared/lib/hooks/useDebouncedValue';
import {
  FilterFieldConfig,
  FilterValues,
  GeneratikTable,
  getColumnsFitWidth,
  getDialogTableWidth,
  useGeneratikTableFilters,
} from '@/shared/ui/GeneratikTable';
import { FONT_FAMILY } from '@/shared/ui/form-fields/fieldStyles';

import { usePickupPointsQuery, useShipmentsSearchQuery } from '../../../api/useShippingQueries';
import {
  AwbListTab,
  filterAwbRows,
  getAwbStatusOptions,
  selectAwbTabRows,
} from '../../../model/allAwbs';
import AwbDialogHeader from '../AwbDialogHeader';
import ShipmentDetailsDialog from '../ShipmentDetailsDialog';
import AwbNotice from '../awb/AwbNotice';
import { generateAwbResultPaperSx } from '../styles';
import ShipmentTimelinePanel from './ShipmentTimelinePanel';
import useAllAwbsColumns from './useAllAwbsColumns';

interface AllAwbsDialogProps {
  onClose: () => void;
  initialTab?: AwbListTab;
}

const TABS: { id: AwbListTab; label: string }[] = [
  { id: 'active', label: 'awb.tab_active' },
  { id: 'history', label: 'awb.tab_history' },
];

const SENDER_FILTER = 'sender';
const STATUS_FILTER = 'status';
const CREATED_FILTER = 'created';

const asList = (value: FilterValues[string]): string[] => (Array.isArray(value) ? value : []);
const FETCH_SIZE = 500;
const SEARCH_DEBOUNCE_MS = 300;
const DIALOG_HEIGHT = 'min(49rem, 90dvh)';

const TabButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  fontFamily: FONT_FAMILY,
  fontSize: '1rem',
  fontWeight: 350,
  lineHeight: 1.25,
  color: active ? theme.palette.blue[500] : theme.palette.grey[950],
  borderBottom: `1px solid ${active ? theme.palette.blue[500] : 'transparent'}`,
  paddingBottom: '0.125rem',
}));

const AllAwbsDialog: FC<AllAwbsDialogProps> = ({ onClose, initialTab = 'active' }) => {
  const { t } = useTranslation();

  const [tab, setTab] = useState<AwbListTab>(initialTab);
  const [search, setSearch] = useState('');
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [expandedIds, setExpandedIds] = useState<GridRowId[]>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false,
  });
  const [viewed, setViewed] = useState<ShipmentResponse | null>(null);

  const senderIds = asList(filterValues[SENDER_FILTER]);
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const { data: page, isFetching } = useShipmentsSearchQuery({
    clientId: senderIds.length === 1 ? senderIds[0] : undefined,
    search: debouncedSearch || undefined,
    page: 0,
    size: FETCH_SIZE,
  });
  const { data: pickupPoints = [] } = usePickupPointsQuery();

  const senderNameByClientId = useMemo(
    () =>
      pickupPoints.reduce<Record<string, string>>((map, point) => {
        map[point.fanClientId] = point.name ?? point.fanClientId;
        return map;
      }, {}),
    [pickupPoints],
  );

  const tabRows = useMemo(() => selectAwbTabRows(page?.content ?? [], tab), [page, tab]);

  const rows = useMemo(() => {
    const created = filterValues[CREATED_FILTER] as { start?: string; end?: string } | undefined;
    return filterAwbRows(tabRows, {
      search,
      clientIds: asList(filterValues[SENDER_FILTER]),
      statusKeys: asList(filterValues[STATUS_FILTER]),
      from: created?.start,
      to: created?.end,
    });
  }, [filterValues, search, tabRows]);

  const statusLabels = useMemo(
    () => getAwbStatusOptions(tabRows).map((option) => t(option.labelKey)),
    [t, tabRows],
  );

  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        id: SENDER_FILTER,
        label: t('Pickup point'),
        type: 'autocomplete',
        multiple: true,
        options: pickupPoints.map((point) => ({
          value: point.fanClientId,
          label: point.name ?? point.fanClientId,
        })),
      },
      {
        id: STATUS_FILTER,
        label: t('Status'),
        type: 'autocomplete',
        multiple: true,
        options: getAwbStatusOptions(tabRows).map((option) => ({
          value: option.key,
          label: t(option.labelKey),
        })),
      },
      {
        id: CREATED_FILTER,
        label: t('awb.column_created_at'),
        type: 'date-range',
        startPlaceholder: t('from'),
        endPlaceholder: t('to'),
      },
    ],
    [pickupPoints, t, tabRows],
  );

  const resetPage = useCallback(
    () => setPaginationModel((current) => ({ ...current, page: 0 })),
    [],
  );

  const changeFilterValues = useCallback(
    (values: FilterValues) => {
      resetPage();
      setFilterValues(values);
    },
    [resetPage],
  );

  const { handleFilterApply, handleFilterReset } = useGeneratikTableFilters({
    filterFields,
    filterValues,
    setFilterValues: changeFilterValues,
  });

  const toggleExpanded = useCallback(
    (id: string) =>
      setExpandedIds((current) =>
        current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
      ),
    [],
  );

  const { columns, sortOptions } = useAllAwbsColumns({
    senderNameByClientId,
    expandedIds,
    onToggle: toggleExpanded,
    onView: setViewed,
    statusLabels,
  });

  const widestColumnsWidth = useRef(0);
  widestColumnsWidth.current = Math.max(widestColumnsWidth.current, getColumnsFitWidth(columns));

  const renderDetailPanel = useCallback(
    ({ row }: { row: ShipmentResponse }) => <ShipmentTimelinePanel shipment={row} />,
    [],
  );

  const getRowId = useCallback((row: ShipmentResponse) => row.id, []);

  const changeTab = (next: AwbListTab) => {
    setTab(next);
    setFilterValues((current) => ({ ...current, [STATUS_FILTER]: undefined }));
    setExpandedIds([]);
    resetPage();
  };

  const tabsSlot = (
    <Stack direction="row" gap="0.75rem" role="tablist" alignItems="center">
      {TABS.map((item) => (
        <TabButton
          key={item.id}
          role="tab"
          aria-selected={tab === item.id}
          active={tab === item.id}
          onClick={() => changeTab(item.id)}
          data-testid={`all-awbs-tab-${item.id}`}
        >
          {t(item.label)}
        </TabButton>
      ))}
    </Stack>
  );

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth={false}
        PaperProps={{
          'data-testid': 'all-awbs-dialog',
          sx: {
            ...generateAwbResultPaperSx,
            width: getDialogTableWidth(widestColumnsWidth.current),
            maxWidth: 'calc(100% - 2rem)',
            height: DIALOG_HEIGHT,
          },
        }}
      >
        <AwbDialogHeader
          icon={<BoxesIcon />}
          title={t('awb.dialog_title')}
          subtitle={t('awb.all_pickup_locations')}
          subtitleNoWrap={false}
          onClose={onClose}
          closeTestId="all-awbs-close"
        />

        {(page?.totalElements ?? 0) > FETCH_SIZE && (
          <AwbNotice tone="info" data-testid="all-awbs-truncated">
            {t('awb.list_truncated', { count: FETCH_SIZE })}
          </AwbNotice>
        )}

        <Box flex={1} minHeight={0}>
          <GeneratikTable<ShipmentResponse>
            columns={columns}
            rows={rows}
            rowCount={rows.length}
            getRowId={getRowId}
            loading={isFetching}
            paginationMode="client"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortingMode="client"
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            sortOptions={sortOptions}
            showSearch
            searchValue={search}
            onSearchChange={(value) => {
              resetPage();
              setSearch(value);
            }}
            searchPlaceholder={t('awb.search_placeholder')}
            showColumns={false}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(model) =>
              setColumnVisibilityModel({ ...model, [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false })
            }
            filterFields={filterFields}
            filterValues={filterValues}
            onFilterValuesChange={changeFilterValues}
            onFilterApply={handleFilterApply}
            onFilterReset={handleFilterReset}
            renderDetailPanel={renderDetailPanel}
            detailPanelHeight="auto"
            detailPanelExpandedRowIds={expandedIds}
            onDetailPanelExpandedRowIdsChange={setExpandedIds}
            emptyStateMessage={t(tab === 'history' ? 'awb.empty_history' : 'awb.empty_active')}
            height="fill"
            autosizeOnMount={false}
            noTopPadding
            noPadding
            plainToolbar
            toolbarStartContent={tabsSlot}
          />
        </Box>
      </Dialog>

      {!!viewed && <ShipmentDetailsDialog shipment={viewed} onClose={() => setViewed(null)} />}
    </>
  );
};

export default AllAwbsDialog;
